import React, { useEffect, useRef, useState } from "react";
import styles from "./SettingsPage.module.css";
import { BASE_URL } from "../../App";

/* Assets */
import pfp_default from "../../assets/pfp_default.png";
import NavBar from "../../components/NavBar/NavBar";

export default function SettingsPage() {
    const fileInputRef = useRef(null);
    const formRef = useRef(null);
    const [photoPreview, setPhotoPreview] = useState(pfp_default);

    const passwordMeetsPolicy = (pw) => {
        const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
        return re.test(pw);
    };

    const [flash, setFlash] = useState({ type: "", text: "" });
    const showFlash = (text, type = "success") => {
        setFlash({ type, text });
    };


// --- NEW: Effect to automatically dismiss flash messages after 5 seconds ---
    useEffect(() => {
        // Only proceed if there is a message to show
        if (flash.text) {
            const timer = setTimeout(() => {
                // Clear the message
                setFlash({ type: "", text: "" });
            }, 5000); // 5000 milliseconds = 5 seconds

            // Cleanup function to clear the timer if the component unmounts
            // or the flash state changes before the timeout fires
            return () => clearTimeout(timer);
        }
    }, [flash.text]); // Dependency array: run this effect whenever flash.text changes
    // ------------------------------------------------------------------------


    useEffect(() => {
        let isCancelled = false;
        (async () => {
            try {
                const res = await fetch(
                    `${BASE_URL}/backend/update_profile.php`,
                    { method: "GET", credentials: "include" }
                );
                const data = await res.json();

                if (!isCancelled && data?.success && data?.profile) {
                    const p = data.profile;
                    const form = formRef.current;

                    if (form) {
                        if (form.elements.name) form.elements.name.value = p.name || "";
                        if (form.elements.username) form.elements.username.value = p.username || "";
                        if (form.elements.email) form.elements.email.value = p.email || "";
                        if (form.elements.age) form.elements.age.value = p.age ?? "";
                        if (form.elements.occupation) form.elements.occupation.value = p.occupation || "";
                        if (form.elements.about) form.elements.about.value = p.about || "";
                        if (form.elements.gender) form.elements.gender.value = p.gender || "";
                        if (form.elements.petFriendly) form.elements.petFriendly.checked = !!p.pet_friendly;
                        if (form.elements.noSmoking) form.elements.noSmoking.checked = !!p.no_smoking;
                    }

                    if (p.avatar_path) {
                        setPhotoPreview(`${BASE_URL}/backend/${p.avatar_path}`);
                    } else {
                        setPhotoPreview(pfp_default);
                    }
                } else {
                    if (!isCancelled && data?.error) {
                        showFlash(`Could not load profile: ${data.error}`, "error");
                    }
                }
            } catch {
                if (!isCancelled) showFlash("Could not load profile. Please refresh.", "error");
            }
        })();
        return () => { isCancelled = true; };
    }, []);

    async function handleSubmit(e) {
        e.preventDefault();
        const form = e.target;

        const pw = form.password?.value || "";
        const conf = form.confirm_password?.value || "";
        const email = form.email?.value || "";

        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            showFlash("Please enter a valid email address.", "error");
            return;
        }

        if (pw) {
            if (!passwordMeetsPolicy(pw)) {
                showFlash(
                    "Password must be 8+ chars and include uppercase, lowercase, number, and one special character.",
                    "error"
                );
                return;
            }
            if (conf !== pw) {
                showFlash("Passwords do not match", "error");
                return;
            }
        }

        const fd = new FormData(form);
        fd.set("petFriendly", form.petFriendly.checked ? "1" : "0");
        fd.set("noSmoking", form.noSmoking.checked ? "1" : "0");

        try {
            const res = await fetch(
                `${BASE_URL}/backend/update_profile.php`,
                { method: "POST", body: fd, credentials: "include" }
            );
            const data = await res.json();

            if (data?.success) {
                showFlash("Profile updated successfully!", "success");
            } else {
                showFlash(`Error: ${data?.error || "Could not update profile."}`, "error");
            }
        } catch {
            showFlash("Something went wrong. Please try again.", "error");
        }
    }

    const handleChangePhotoClick = () => {
        fileInputRef.current?.click();
    };

    const handlePhotoSelected = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const previewUrl = URL.createObjectURL(file);
        setPhotoPreview(previewUrl);
    };

    async function handleLogout() {
        try {
            const res = await fetch(`${BASE_URL}/backend/logout.php`, {
                method: "POST",
                credentials: "include",
            });
            const data = await res.json();

            if (data.success) {
                window.location.href = `${BASE_URL}`;
            } else {
                showFlash("Could not log out. Please try again.", "error");
            }
        } catch {
            showFlash("Error logging out. Please try again.", "error");
        }
    }

    return (
        <div className={styles.page}>
            <NavBar />

            <main className={styles.container} style={{ paddingBottom: 24 }}>
                <form ref={formRef} onSubmit={handleSubmit} id="settingsForm" encType="multipart/form-data">
                    
                    <div className={styles.logoutTopWrap}>
                        <button
                            type="button"
                            className={styles.logoutBtnTop}
                            onClick={handleLogout}
                        >
                            Log Out
                        </button>
                    </div>

                    <div className={styles.profilePhoto}>
                        <img src={photoPreview} alt="Profile" className={styles.profileImg} />
                        <button type="button" className={styles.changeLink} onClick={handleChangePhotoClick}>
                            Change Profile Photo
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            name="avatar"
                            accept="image/*"
                            className={styles.hiddenInput}
                            onChange={handlePhotoSelected}
                        />
                    </div>

                    <div className="input-group">
                        <label htmlFor="name">Name</label>
                        <input
                            id="name"
                            name="name"
                            type="text"
                            maxLength={80}
                            pattern={`[A-Za-zÀ-ÿ' -]{1,80}`}
                            title="Letters, spaces, apostrophes, and hyphens only"
                        />
                    </div>

                    {/* ✅ Username Field Added */}
                    <div className="input-group">
                        <label htmlFor="username">Username</label>
                        <input
                            id="username"
                            name="username"
                            type="text"
                            placeholder="yourusername"
                            maxLength={30}
                            pattern="^[A-Za-z0-9_]{3,30}$"
                            title="Letters, numbers, and underscores only (3–30 characters)"
                        />
                    </div>

                    <div className="input-group">
                        <label>Change Email</label>
                        <input type="email" name="email" placeholder="example@example.com" />
                    </div>

                    <div className="input-group">
                        <label>Change Password</label>
                        <input type="password" name="password" placeholder="New Password Here" />
                    </div>

                    <div className="input-group">
                        <label>Confirm Password</label>
                        <input type="password" name="confirm_password" placeholder="Confirm Password Here" />
                    </div>

                    <div className={styles.inlineControls}>
                        <div className={styles.genderWrap}>
                            <label htmlFor="gender">Gender</label>
                            <select id="gender" name="gender" defaultValue="">
                                <option value="" disabled>Select gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>

                        <label htmlFor="petFriendly" className={styles.checkField}>
                            <input id="petFriendly" name="petFriendly" type="checkbox" />
                            <span>Pet Friendly</span>
                        </label>

                        <label htmlFor="noSmoking" className={styles.checkField}>
                            <input id="noSmoking" name="noSmoking" type="checkbox" />
                            <span>No Smoking</span>
                        </label>
                    </div>

                    <div className="input-group">
                        <label htmlFor="age">Age</label>
                        <input
                            id="age"
                            type="number"
                            name="age"
                            min="18"
                            max="120"
                            placeholder="You must be at least 18 years of age to use HomeMate"
                        />
                    </div>

                    <div className="input-group">
                        <label htmlFor="occupation">Major/Occupation</label>
                        <input id="occupation" type="text" name="occupation" />
                    </div>

                    <div className="input-group">
                        <label htmlFor="about">Describe Yourself</label>
                        <textarea
                            id="about"
                            name="about"
                            rows={8}
                            maxLength={500}
                            style={{ width: "100%", resize: "vertical" }}
                        />
                    </div>

                    <button type="submit" className={styles.confirmBtn}>
                        Confirm Changes
                    </button>

                    {flash.text && (
                        <div
                            className={flash.type === "success" ? styles.alertSuccess : styles.alertError}
                            role="status"
                        >
                            {flash.text}
                        </div>
                    )}
                </form>
            </main>
        </div>
    );
}
